import {
  AudioModule,
  RecordingOptions,
  RecordingPresets,
  useAudioRecorder,
} from "expo-audio";
import { useCallback, useRef, useState } from "react";

import { captureException, logger } from "@/sentry";
import { logMediaPermissions } from "@/utils/permissions";
export type RecordingState = "idle" | "recording" | "processing";

export interface UseVoiceRecordingResult {
  recordingState: RecordingState;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string | null>;
  cancelRecording: () => Promise<void>;
  recordingDuration: number;
  hasPermission: boolean | null;
  mediaStream: MediaStream | null;
}

export interface UseVoiceRecordingOptions {
  onAutoStop?: (audioUri: string | null) => void;
}

// Configuration for auto-stop detection
const MIN_RECORDING_DURATION_MS = 500; // Minimum 0.5 seconds to avoid accidental taps

export const useVoiceRecording = (
  options?: UseVoiceRecordingOptions,
): UseVoiceRecordingResult => {
  const [recordingState, setRecordingState] = useState<RecordingState>("idle");
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const recordingStartTimeRef = useRef<number | null>(null);

  const audioRecorder = useAudioRecorder({
    ...RecordingPresets.HIGH_QUALITY,
    isMeteringEnabled: true,
  });

  const requestPermission = useCallback(async (): Promise<boolean> => {
    try {
      const permission = await AudioModule.requestRecordingPermissionsAsync();
      setHasPermission(permission.granted);
      return permission.granted;
    } catch (error) {
      logger.error("Error requesting audio permission", {
        category: "voice",
        error,
      });
      setHasPermission(false);
      return false;
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      // Check/request permission first
      const permitted = hasPermission ?? (await requestPermission());
      if (!permitted) {
        throw new Error("Microphone permission denied");
      }
      logMediaPermissions();

      // NOTE: Omits web mimeType, letting browser pick its own favorite. This fixes older browsers where webm is not supported.
      // As of Expo 54, web option cannot be overridden from the useAudioRecorder hook init, this is the next best location
      ((audioRecorder as any).options as RecordingOptions).web = {
        bitsPerSecond: RecordingPresets.HIGH_QUALITY.web?.bitsPerSecond,
      };
      await audioRecorder.prepareToRecordAsync();

      recordingStartTimeRef.current = Date.now();
      setRecordingState("recording");
      await audioRecorder.record();
    } catch (error) {
      setRecordingState("idle");
      recordingStartTimeRef.current = null;
      throw error;
    }
  }, [audioRecorder, hasPermission, requestPermission]);

  const stopRecording = useCallback(async (): Promise<string | null> => {
    try {
      setRecordingState("processing");
      await audioRecorder.stop();

      // Check if recording meets minimum duration
      const recordingDuration = recordingStartTimeRef.current
        ? Date.now() - recordingStartTimeRef.current
        : 0;

      recordingStartTimeRef.current = null;

      if (recordingDuration < MIN_RECORDING_DURATION_MS) {
        // Recording too short - discard it
        logger.info("Recording discarded, too short", {
          category: "voice",
        });
        setRecordingState("idle");

        return null;
      }

      const uri = audioRecorder.uri ?? null;
      setRecordingState("idle");

      return uri;
    } catch (error) {
      captureException("Error stopping recording", {
        extra: {
          category: "voice",
          error,
        },
      });
      setRecordingState("idle");
      recordingStartTimeRef.current = null;
      return null;
    }
  }, [audioRecorder]);

  const cancelRecording = useCallback(async () => {
    try {
      await audioRecorder.stop();
      recordingStartTimeRef.current = null;
      setRecordingState("idle");
    } catch (error) {
      captureException("Error canceling recording", {
        extra: {
          category: "voice",
          error,
        },
      });
      recordingStartTimeRef.current = null;
      setRecordingState("idle");
    }
  }, [audioRecorder]);

  return {
    recordingState,
    startRecording,
    stopRecording,
    cancelRecording,
    recordingDuration: audioRecorder.isRecording
      ? audioRecorder.currentTime
      : 0,
    hasPermission,
    // On web, expo-audio's AudioRecorderWeb exposes the underlying MediaRecorder
    // which holds the already-open MediaStream. We reuse it here so VoiceVisualizer
    // can run FFT analysis without requesting a second getUserMedia stream.
    // On native, mediaRecorder is undefined so this returns null.
    mediaStream: (audioRecorder as any).mediaRecorder?.stream ?? null,
  };
};
